import Styles from './Checkout.module.scss';
import { useState, useEffect, useRef, useContext } from 'react';

// Module imports.
import axios from 'axios';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import { TextField, InputAdornment, IconButton, Alert, AlertTitle, Divider, Tooltip } from '@mui/material';
import ClearIcon from '@mui/icons-material/Clear';

// Components import.
import { AuthContext, Message, ViewContext } from 'components/lib';
import { LoadingBackdrop } from 'components/global/LoadingBackdrop/LoadingBackdrop';
import { Checklist, Delete, Discount, InfoOutlined, LocalActivity, ShoppingCart } from '@mui/icons-material';
import { AivagamButton } from '../AivagamButton/AivagamButton';

/**
 * Checkout component to direct user to payment
 * @param {function} removeItemAction parent function to handle delete clicks on items
 * @param {string} checkoutName  title shown on the checkout page
 * @param {Set}  userSelItems set with the list of items selected by user
 * @param {list} availableItems list of items available for sale
 * @param {boolean} individualPricing set whether the individial prices of the selected Items needs to be shown
 * @param {string} purchaseEndpoint set the end point to which the checkout request should go to
 * @param {Array<object>} selectedPrices list of objects containing the price details of the selected items
 * @returns 
 */
export function Checkout({
    isBundle,
    purchaseEndpoint,
    removeItemAction,
    checkoutName,
    onClose,
    availableItems,
    isProject,
    enrolledLabs,
    userSelItems,
    ...props
}) {
    const [selectedItems, setSelectedItems] = useState(new Set());
    const [offeredItems, setOfferedItems] = useState([])
    const [isLoadingOpen, setIsLoadingOpen] = useState(false);
    const [couponData, setCouponData] = useState({
        code: '',
        isApplied: false,
    });

    const [totals, setTotals] = useState({
        strikedTotal: 0,
        subTotal: 0,
        productDiscount: 0,
        existingProductDiscount: 0,
        couponDiscount: 0,
        grandTotal: 0,
        totalSavings: 0,
        savingsPercent: 0,
    })

    const context = useRef(useContext(ViewContext));
    const viewContext = useContext(ViewContext);
    const authContext = useContext(AuthContext);
    const currencySymbol = authContext?.user.currencySymbol;

    useEffect(() => {
        if (availableItems) {
            setOfferedItems(availableItems)

        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [availableItems]);

    useEffect(() => {
        if (userSelItems) {
            setSelectedItems(prev => new Set([...prev, ...userSelItems]));
        }
    }, [userSelItems]);

    useEffect(() => {

        if (!offeredItems || offeredItems.length === 0) return;
        const isMultiple = offeredItems.length > 1;

        let strikedTotal = 0;
        let subTotal = 0;

        const price = offeredItems[0]?.pricing || {};

        if (isMultiple) {
            // When cart has multiple products, it will be treated
            // Applicalbe for stream and bundle coupon purchases(will add to future)
            let selectedIds = []
            offeredItems.forEach(item => {
                const price = item.pricing;
                strikedTotal += price?.actual_price ?? 0;
                subTotal += price?.price ?? 0;
                selectedIds.push(item.id);
            });
            setSelectedItems(new Set(selectedIds));

        } else {
            // single product purchase price calculation
            strikedTotal = price.actual_price ?? 0;
            subTotal = price.price ?? 0;
            setSelectedItems(prev => new Set([price.product_id, ...prev]));
        }
        const productDiscount = Math.max(strikedTotal - subTotal, 0);
        
        const basePrice = price?.meta_data?.base_price;
        let existingProductDiscount = 0;
        if(basePrice && enrolledLabs){
            existingProductDiscount = basePrice * enrolledLabs;
        }
        const couponDiscount = couponData.isApplied
            ? couponData.couponDiscount : 0;
        const grandTotal = Math.max(subTotal - couponDiscount - existingProductDiscount, 0);
        const totalSavings = Math.max(strikedTotal - grandTotal, 0);
        const savingsPercent = strikedTotal > 0 ?
            Math.round((totalSavings / strikedTotal) * 100) : 0;
        setTotals({
            strikedTotal,
            subTotal,
            productDiscount,
            couponDiscount,
            grandTotal,
            totalSavings,
            savingsPercent,
            existingProductDiscount
        })
    }, [offeredItems, couponData.couponDiscount])
    const handleApplyCoupon = async () => {
        if (!couponData.code.trim()) {
            viewContext.notification.show(
                viewContext.t('Components.Checkout.coupon.empty'),
                'warning', true
            );
            return;
        }

        setIsLoadingOpen(true);

        try {
            const options = {
                method: 'POST',
                url: `/api/coupons/verify`,
                headers: {
                    accept: 'application/json',
                },
                data: {
                    items: [...selectedItems],
                    couponCode: couponData.code.trim(),
                    type: offeredItems[0]?.pricing.product_type || ''
                },
            };

            const response = await axios.request(options);
            const data = response.data;

            if (response.data?.valid) {
                const { discountAmount } = data;
                setCouponData((prev) => ({ ...prev, isApplied: true, couponDiscount: discountAmount }));

                viewContext.notification.show(
                    viewContext.t('Components.Checkout.coupon.success', { code: couponData.code }), 'success', true
                );
            } else {
                viewContext.notification.show(
                    viewContext.t('Components.Checkout.coupon.invalid'), 'error', true
                );
            }
        } catch (err) {
            if (err.response?.data?.error) {
                context.current.handleError(err.response.data.error);
            } else {
                context.current.handleError(err);
            }
        } finally {
            setIsLoadingOpen(false);
        }
    };

    const handleRemoveCoupon = () => {
        setCouponData({ code: '', isApplied: false });

        setTotals(prev => ({
            ...prev,
            couponDiscount: 0,
            grandTotal: prev.subTotal, // restore to subtotal
        }));

        viewContext.notification.show(viewContext.t('Components.Checkout.coupon.removed'), 'info', true);
    };

    const handleCheckout = () => {
        setIsLoadingOpen(true);
        const options = {
            method: 'POST',
            url: `${purchaseEndpoint}`,
            headers: {
                accept: 'application/json',
            },
            data: {
                quantity: selectedItems.size,
                items: [...selectedItems],
                couponCode: couponData.code.trim(),
                isProject: isProject || 0
            },
        };

        axios
            .request(options)
            .then(function (response) {
                if (response.data?.checkout_url) {
                    setIsLoadingOpen(false);
                    window.location.href = response.data?.checkout_url
                }
            })
            .catch((err) => {
                setIsLoadingOpen(false);
                if (err.response.data.error) {
                    context.current.handleError(err.response.data.error);
                } else {
                    context.current.handleError(err.response.data);
                }

            });
    }

    const isCheckoutEnabled = couponData.isApplied || couponData.code.trim() === '';

    return (
        <div
            id='ancestorContainer'
            data-testid="checkout-ancestorContainer-container"
            className={Styles.ancestorContainer}
        >
            <LoadingBackdrop isOpen={isLoadingOpen} />
            {selectedItems.size > 0 ? (
                <>
                    <div id='cartContainer' className={Styles.cartContainer}>
                        <h1 id='cartTitle' className={Styles.cartTitle}>
                            <ShoppingCart fontSize='large' /> Your Cart
                        </h1>
                    </div>
                    <div id='productCard' className={Styles.productCard}>
                        <div id='productsTitle' className={Styles.productTitle}>
                            Items
                        </div>
                        {offeredItems
                            .map((item) => (
                                <div id='itemCard' key={item.id} className={Styles.itemCard}>
                                    <div id='itemDescription' className={Styles.itemDescription}>
                                        <img
                                            id='itemImg'
                                            className={Styles.itemImg}
                                            src={`${process.env.REACT_APP_GLOBAL_MEDIA_URL}${item.icon}`}
                                        />
                                        <div id='itemDetails' className={Styles.itemDetails}>
                                            <div id='itemName' className={Styles.itemName}>
                                                {item.name}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ))}

                    </div>
                    <div id='couponCard' className={Styles.productCard}>
                        <div id='applyCouponTitle' className={Styles.productTitle}>
                            <span id='applyCouponText' className={Styles.applyCouponText}>Apply Coupon <Tooltip title='Coupon will be applied on Subtotal' placement='top'><InfoOutlined fontSize='small' /></Tooltip></span>
                        </div>
                        <div id='couponInputContainer' className={Styles.couponInputContainer}>
                            <TextField
                                id='couponInput'
                                variant='outlined'
                                fullWidth
                                placeholder='Enter Coupon Code'
                                value={couponData.code}
                                onChange={(e) => {
                                    setCouponData((prev) => ({ ...prev, code: e.target.value }))
                                }}
                                disabled={couponData.isApplied}
                                InputProps={{
                                    endAdornment: (couponData.code && !couponData.isApplied) && (
                                        <InputAdornment position='end' sx={{ mr: '4px' }}>
                                            <IconButton
                                                title='clear'
                                                size='small'
                                                onClick={() => {
                                                    setCouponData((prev) => ({ ...prev, code: '' }))
                                                }}
                                            >
                                                <ClearIcon fontSize='small' />
                                            </IconButton>
                                        </InputAdornment>
                                    )
                                }}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        height: '40px',
                                        '& .input': {
                                            height: '40px',
                                        }
                                    }
                                }}
                            />
                            <AivagamButton
                                type='action'
                                buttonText={couponData.isApplied ? 'Applied' : 'Apply'}
                                disabled={!couponData.code || couponData.isApplied}
                                onClick={handleApplyCoupon}
                                classStyles={{
                                    boxShadow: '0 4px 6px -2px rgba(0, 0,0,0.2)',
                                    width: '8em',
                                    borderRadius: '8px',
                                    fontWeight: 'bold'
                                }}
                            />
                            {/* Render remove coupon only if coupon is applied */}
                            {!!couponData.isApplied && (
                                <IconButton
                                    size='medium'
                                    onClick={handleRemoveCoupon}
                                    sx={{
                                        backgroundColor: '#f6e4e6ff',
                                        '&:hover': {
                                            backgroundColor: '#f6d6d6ff',
                                        },
                                        borderRadius: '8px'
                                    }}
                                >
                                    <Delete fontSize='inherit' sx={{ color: 'darkred' }} />
                                </IconButton>
                            )}
                        </div>
                        {!!couponData.isApplied && (
                            <Alert icon={<LocalActivity fontSize='inherit' />} severity='success' sx={{ borderRadius: '8px' }}>
                                <AlertTitle>Coupon <strong>{couponData.code}</strong> applied!</AlertTitle>
                                You're saving an additional <strong>{currencySymbol}{totals.couponDiscount}</strong> 🎉
                            </Alert>
                        )}

                    </div>
                    <div id='priceSummaryCard' className={Styles.productCard}>
                        <div id='priceSummaryTitle' className={Styles.productTitle}>
                            Price Summary
                        </div>
                        <div id='priceDetails' className={Styles.priceDetails}>
                            <div id="totalOriginal" className={Styles.price}>
                                <span id='totalPriceText'>Total Product Price</span>
                                <span id='totalPrice'>{currencySymbol}{totals.strikedTotal}</span>
                            </div>
                            <div id="productDiscount" className={Styles.discountPrice}>
                                <span id='discountPriceText' className={Styles.discountDetail}><Discount fontSize='small' />Discount</span>
                                <span id='discountPrice'>- {currencySymbol}{totals.productDiscount || '0'}</span>
                            </div>
                            <Divider id='priceDetailsDivider' />
                            <div id="subtotal" className={Styles.price}>
                                <span id='subtotalText' >Subtotal</span>
                                <span id='subtotalPrice'>{currencySymbol}{totals.subTotal}</span>
                            </div>
                            {!!totals.couponDiscount && (
                                <div id="couponDiscount" className={Styles.discountPrice}>
                                    <span id='couponDiscountText' className={Styles.discountDetail}><LocalActivity fontSize='small' />Coupon Discount ({couponData.code})</span>
                                    <span id='couponDiscountPrice'>- {currencySymbol}{totals.couponDiscount || '0'}</span>
                                </div>
                            )}
                            {!!totals.existingProductDiscount && (
                                <div id="productDiscount" className={Styles.discountPrice}>
                                    <span id='discountPriceText' className={Styles.discountDetail}><Checklist fontSize='small' />Existing Product Discount ({enrolledLabs} Items)</span>
                                    <span id='discountPrice'>- {currencySymbol}{totals.existingProductDiscount || '0'}</span>
                                </div>
                            )}
                        </div>
                    </div>
                    <div id='grandTotalCard' className={Styles.productCard}>
                        <div id='grandTotalDetails' className={Styles.grandTotalDetails}>
                            <div id='grandTotal' className={Styles.grandTotal}>
                                <span id='grandTotalText' className={Styles.grandTotalText}>Grand Total</span>
                                <span id='grandTotalPrice' className={Styles.grandTotalText}>{currencySymbol}{totals.grandTotal}</span>
                            </div>
                        </div>
                        <Divider id='grandTotalDivider' />
                        <div id='totalSavings' className={Styles.totalSavings}>
                            <span id='totalSavingsText' className={Styles.totalSavingsText}>Total Savings</span>
                            <span id='totalSavingsPrice' className={Styles.totalSavingsText}>{currencySymbol}{totals.totalSavings} ({totals.savingsPercent}% OFF)</span>
                        </div>
                    </div>
                    <div className={Styles.actionButtonsContainer}>
                        <button
                            type="button"
                            className={Styles.cancelButton}
                            onClick={() => onClose && onClose()}
                        >
                            {viewContext.t('Components.Checkout.cancel')}
                        </button>
                        <button
                            className={Styles.checkoutButton}
                            type="submit"
                            onClick={handleCheckout}
                            disabled={!isCheckoutEnabled}
                        >
                            {viewContext.t('Components.Checkout.button')}
                        </button>
                    </div>
                </>
            ) : (
                <div className={Styles.nothingWarning}>
                    <Message
                        type='info'
                        title={viewContext.t('Components.Checkout.message.title', { checkoutName: checkoutName })}
                        text={viewContext.t('Components.Checkout.message.text')}
                    />
                </div>
            )}
        </div>
    );
}